import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createLoveScene } from "../../lib/loveScene";
import { audio } from "../../lib/audio";
import { music } from "../../lib/orchestra";
import { mixHex } from "../../lib/color";
import { Moon, TwoBirds } from "./TwoBirds";
import { CozyHome } from "./CozyHome";
import { useIdle } from "./useIdle";

const STOPS = [
  ["#5b3fa8", "#9b5fd6"],
  ["#9a6fd0", "#f08cc0"],
  ["#d4b8f2", "#ffd6ea"],
];

export default function LoveExperience({ painting, onDone }) {
  const canvasRef = useRef(null);
  const originRef = useRef(null);
  const sceneRef = useRef(null);
  const [warmth, setWarmth] = useState(0);
  const [stage, setStage] = useState("love");
  const [touched, setTouched] = useState(false);
  const idle = useIdle(8000);

  useEffect(() => {
    const scene = createLoveScene(canvasRef.current, {
      getOrigin: () => {
        const r = originRef.current?.getBoundingClientRect();
        return r ? { x: r.left + r.width / 2, y: r.top } : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      },
      onWarmth: (w) => {
        setWarmth(w);
        audio.setWarmth(w);
      },
      onComplete: () => setTimeout(() => setStage("home"), 1800),
    });
    sceneRef.current = scene;
    return () => scene.destroy();
  }, []);

  useEffect(() => {
    if (stage !== "home") return;
    const id = setTimeout(onDone, 9000);
    return () => clearTimeout(id);
  }, [stage, onDone]);

  const tap = (e) => {
    setTouched(true);
    sceneRef.current?.burst(e.clientX, e.clientY);
    music.chime();
  };

  const bg = `linear-gradient(to bottom, ${STOPS.map(([a, b]) => mixHex(a, b, warmth)).join(", ")})`;

  return (
    <div data-testid="love-experience" data-stage={stage} data-warmth={Number(warmth || 0).toFixed(2)} className="fixed inset-0 z-50 overflow-hidden" style={{ background: bg }}>
      <motion.div
        data-testid="experience-love-trigger"
        animate={{ opacity: stage === "love" ? 1 : 0 }}
        transition={{ duration: 1.6 }}
        className={`absolute inset-0 touch-none select-none ${stage === "love" ? "cursor-pointer" : "pointer-events-none"}`}
        onPointerDown={tap}
      >
        <Moon warmth={warmth} />
        <div className="absolute inset-x-0 top-[44%] flex -translate-y-[10%] justify-center">
          <TwoBirds warmth={warmth} originRef={originRef} />
        </div>
        <canvas ref={canvasRef} data-testid="love-hearts-canvas" className="pointer-events-none absolute inset-0 h-full w-full" />
      </motion.div>
      <AnimatePresence>
        {(!touched || idle) && stage === "love" && (
          <motion.p
            data-testid="play-hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, delay: 1.5 }}
            className="pointer-events-none absolute inset-x-0 bottom-24 text-center font-display text-2xl italic text-[#3B2656]/85 sm:text-3xl"
          >
            touch the space between them
          </motion.p>
        )}
      </AnimatePresence>
      {stage === "home" && <CozyHome painting={painting} />}
    </div>
  );
}
